import React, { useState, useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import axios from "axios";
import "./GiftSuccessPage.css";

const getGiftTypeDisplay = (gift) => {
  if (gift.displayName) return gift.displayName;
  if (gift.giftType?.includes("shared")) {
    return gift.giftType.includes("package")
      ? "Flexible Package"
      : "Flexible Drop-In";
  }
  return gift.giftType?.includes("package") ? "Package" : "Drop-in Session";
};

const getGiftServiceDisplay = (gift) => {
  if (gift.giftType?.includes("shared")) {
    return "Flexible Session Credits";
  }
  return gift.giftType?.includes("sauna")
    ? "Infrared Sauna"
    : "Red Light Therapy";
};

const GiftSuccessPage = () => {
  const location = useLocation();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [gifts, setGifts] = useState(null);

  // Get session_id from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get("session_id");

  // Get gifts from location state (old method)
  const giftsFromState = location.state?.gifts;

  useEffect(() => {
    // If we have gifts from state, use those
    if (giftsFromState) {
      setGifts(giftsFromState);
      return;
    }

    // If we have a session_id, fetch the gifts
    if (sessionId && token) {
      const fetchGifts = async () => {
        setLoading(true);
        setError("");

        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/gifts/session/${sessionId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.data && response.data.gifts) {
            setGifts(response.data.gifts);
          } else {
            setError("No gift information found");
          }
        } catch (err) {
          console.error("Error fetching gift details:", err);
          setError(
            "Failed to load gift details. Please check your account for gift information."
          );
        } finally {
          setLoading(false);
        }
      };

      fetchGifts();
    }
  }, [sessionId, token, giftsFromState]);

  // Show loading state
  if (loading) {
    return (
      <div className="gift-success-page">
        <div className="loading-spinner">Loading gift details...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="gift-success-page">
        <div className="error-message">{error}</div>
        <div className="success-actions">
          <Link to="/member" className="primary-button">
            Go to My Account
          </Link>
          <Link to="/gift-cards" className="secondary-button">
            Send Another Gift
          </Link>
        </div>
      </div>
    );
  }

  // If no gifts data is available, redirect to the gift cards page
  if (!gifts) {
    return <Navigate to="/gift-cards" />;
  }

  // Get the gift to display details
  const gift = gifts[0];

  // Determine session type for display
  const sessionTypeDisplay = getGiftServiceDisplay(gift);
  const giftTypeDisplay = getGiftTypeDisplay(gift);

  return (
    <div className="gift-success-page">
      <div className="success-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M0 0h24v24H0V0z" fill="none" />
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8z" />
        </svg>
      </div>

      <h1>Gift Successfully Sent!</h1>

      <div className="success-details">
        <p className="success-message">
          You have successfully gifted {sessionTypeDisplay} sessions to{" "}
          {gift.recipient.name || gift.recipient.email}.
        </p>

        <div className="gift-details-card">
          <h2>Gift Details</h2>
          <div className="gift-detail-item">
            <span className="detail-label">Session Type:</span>
            <span className="detail-value">{sessionTypeDisplay}</span>
          </div>
          <div className="gift-detail-item">
            <span className="detail-label">Gift Type:</span>
            <span className="detail-value">{giftTypeDisplay}</span>
          </div>
          <div className="gift-detail-item">
            <span className="detail-label">Sessions Per Recipient:</span>
            <span className="detail-value">{gift.sessionCount}</span>
          </div>
          {gift.note && (
            <div className="gift-detail-item">
              <span className="detail-label">Your Note:</span>
              <p className="gift-note">{gift.note}</p>
            </div>
          )}
        </div>

        <div className="recipient-info">
          <p>
            The recipient has been notified via email with instructions on how
            to redeem their gift.
          </p>
          <p>
            {gift.recipient.memberId
              ? "Since the recipient is already a member, the sessions have been automatically added to their account."
              : "Once the recipient creates an account, they can redeem their gift using the code provided in the email."}
          </p>
        </div>
      </div>

      <div className="success-actions">
        <Link to="/member" className="primary-button">
          Go to My Account
        </Link>
        <Link to="/gift-cards" className="secondary-button">
          Send Another Gift
        </Link>
      </div>
    </div>
  );
};

export default GiftSuccessPage;
